import { createEventsFromSchematics, eventDetail } from "@kells/apis/analytics";
import { InsuranceServiceType } from "../../utils";
import { SubscriptionPlan } from "@kells/interfaces/patient";

interface OpenReportEventInterface {
  patientId: string;
  sessionDate: string;
}

interface OpenProductLinkEventInterface {
  product: string;
  productLink: string;
}

interface InsuranceDetailsModalEventInterface {
  service: InsuranceServiceType;
}

enum EventTypes {
  HomePageOpened = "Home page has been opened",

  OpenSecondOpinion = "Second Opinion opened",

  NavigateToPaymentLink = "Navigate to Stripe Payment Link",

  OpenUpgradePlanModal = "Upgrade plan modal opened",
  CloseUpgradePlanModal = "Upgrade plan modal closed",
  OpenDentalProviderUpgradePlanModal = "Dental provider upgrade plan modal opened",
  CloseDentalProviderUpgradePlanModal = "Dental provider upgrade plan modal closed",
  OpenConfirmUpgradePlanModal = "Confirm upgrade plan modal opened",
  CloseConfirmUpgradePlanModal = "Confirm upgrade plan modal closed",

  BackToPlansClick = "Back to plans click",
  ShowAddressSection = "Upgrade Plan Modal - Show address section",
  PreviewProviderNetworkClick = "Upgrade Plan Modal - Preview provider network click",

  SelectPlanClick = "Select plan click",
  UpgradePlanClick = "Upgrade plan click",

  OpenTermsModal = "Terms and Conditions modal opened",
  CloseTermsModal = "Terms and Conditions modal closed",

  OpenPaymentSuccessModal = "Payment success modal opened",

  OpenAiPhotoScan = "Ai dental Scan opened",

  OpenTalkToDentist = "Confirmation modal for Talk To Dentist opened",
  ConfirmTalkToDentist = "Talk To Dentist open confirmed",
  CloseTalkToDentist = "Confirmation modal for Talk To Dentist cancelled",

  OpenReport = "Report is opened",

  OpenProductRecommendation = "Product recommendation is opened",

  InfoCollectionOpened = "Info collection modal opened [Info Collection]",
  InfoCollectionCompleted = "Info collection modal completed [Info Collection]",

  OnboardingOpened = "Onboarding has been opened [Onboarding]",
  ManualOnboardingTriggered = "Onboarding has been triggered manually [Onboarding]",
  OnboardingStarted = "Onboarding process started [Onboarding]",
  OnboardingReverted = "Onboarding process reverted [Onboarding]",
  OnboardingNavigateForward = "Onboarding navigated forward [Onboarding]",
  OnboardingNavigateBackward = "Onboarding navigated backward [Onboarding]",
  OnboardingNavigateToSlide = "Onboarding navigated to slide [Onboarding]",
  OnboardingClosed = "Onboarding has been closed [Onboarding]",
  OnboardingFinalized = "Onboarding has been completed [Onboarding]",

  OpenProductLinkEvent = "Product link opened [Products Recommendation]",
  OpenProductDiscountModalEvent = "Product discount modal opened [Products Recommendation]",
  CopyProductCouponEvent = "Product coupon copied [Products Recommendation]",

  OpenReportFromDentalRisk = "Open report from dental risk",

  OpenDentalScanFromDentalRisk = "Open dental scan from dental risk",

  OpenVoiceflowChatFromDashboard = "Voiceflow chat opened from dashboard",

  // Insurance
  SavingPlanReferralClick = "saving_plan_referral_click",
  InsuranceInfoModalOpen = "Insurance information modal open",
  InsuranceInfoModalClose = "Insurance information modal close",
  InsuranceServiceDetailsModalOpen = "Insurance service details modal open",
  InsuranceServiceDetailsModalClose = "Insurance service details modal close",
  InsuranceFormModalOpen = "Insurance form modal open",
  InsuranceFormModalClose = "Insurance form modal close",
  InsuranceSubmit = "Insurance submit",
  InsuranceConnectSuccessModalOpen = "Insurance connect success modal open",
  InsuranceConnectSuccessModalClose = "Insurance connect success modal close",
  InsuranceConnectFailModalOpen = "Insurance connect fail modal open",
  InsuranceConnectFailModalClose = "Insurance connect fail modal close",
  InsuranceConnectPlanNotSupportedModalOpen = "Insurance connect plan not supported modal open",
  InsuranceConnectPlanNotSupportedModalClose = "Insurance connect plan not supported modal close",
  DisconnectInsuranceClick = "Disconnect insurance click",
  DisconnectInsuranceModalCancel = "Disconnect insurance modal cancel",
  DisconnectInsuranceModalConfirm = "Disconnect insurance modal confirm",
  DisconnectInsuranceSuccessModalOpen = "Disconnect insurance success modal open",
  DisconnectInsuranceSuccessModalClose = "Disconnect insurance success modal close",
  DisconnectInsuranceErrorModalOpen = "Disconnect insurance error modal open",
  DisconnectInsuranceErrorModalClose = "Disconnect insurance error modal close",

  // Find a Dentist
  FindADentistSearchClick = "Find a Dentist search click",

  // Support
  OpenSupportModal = "Support modal opened",
  CloseSupportModal = "Support modal closed",

  // Onboarding
  HideOnboardingPanelEvent = "Hide onboarding panel event",
  ShowOnboardingPanelEvent = "Show onboarding panel event",
  ToggleOnboardingPanelEvent = "Toggle onboarding panel event",
  OnboardingPanelCompletedEvent = "Onboarding panel completed event",
}

const eventCreators = createEventsFromSchematics({
  HomePageOpenedEvent: {
    displayedName: EventTypes.HomePageOpened,
  },

  OpenSecondOpinionEvent: {
    displayedName: EventTypes.OpenSecondOpinion,
  },

  NavigateToPaymentLink: {
    displayedName: EventTypes.NavigateToPaymentLink,
  },

  OpenUpgradePlanModal: {
    displayedName: EventTypes.OpenUpgradePlanModal,
  },

  CloseUpgradePlanModal: {
    displayedName: EventTypes.CloseUpgradePlanModal,
  },

  OpenDentalProviderUpgradePlanModal: {
    displayedName: EventTypes.OpenDentalProviderUpgradePlanModal,
  },

  CloseDentalProviderUpgradePlanModal: {
    displayedName: EventTypes.CloseDentalProviderUpgradePlanModal,
  },

  OpenConfirmUpgradePlanModal: {
    displayedName: EventTypes.OpenConfirmUpgradePlanModal,
  },

  CloseConfirmUpgradePlanModal: {
    displayedName: EventTypes.CloseConfirmUpgradePlanModal,
  },

  OpenPaymentSuccessModal: {
    displayedName: EventTypes.OpenPaymentSuccessModal,
  },

  OpenAiPhotoScanEvent: {
    displayedName: EventTypes.OpenAiPhotoScan,
  },

  OpenTalkToDentistEvent: {
    displayedName: EventTypes.OpenTalkToDentist,
  },
  ConfirmTalkToDentistEvent: {
    displayedName: EventTypes.ConfirmTalkToDentist,
  },
  CloseTalkToDentistEvent: {
    displayedName: EventTypes.CloseTalkToDentist,
  },

  OpenReportEvent: {
    displayedName: EventTypes.OpenReport,
    detail: eventDetail<OpenReportEventInterface>(),
  },

  OpenProductLinkEvent: {
    displayedName: EventTypes.OpenProductLinkEvent,
    detail: eventDetail<OpenProductLinkEventInterface>(),
  },

  OpenProductDiscountModalEvent: {
    displayedName: EventTypes.OpenProductDiscountModalEvent,
    detail: eventDetail<OpenProductLinkEventInterface>(),
  },

  CopyProductCouponEvent: {
    displayedName: EventTypes.CopyProductCouponEvent,
    detail: eventDetail<OpenProductLinkEventInterface>(),
  },

  OpenProductRecommendationEvent: {
    displayedName: EventTypes.OpenProductRecommendation,
  },

  InfoCollectionOpenedEvent: {
    displayedName: EventTypes.InfoCollectionOpened,
  },
  InfoCollectionCompletedEvent: {
    displayedName: EventTypes.InfoCollectionCompleted,
  },

  OnboardingOpenedEvent: {
    displayedName: EventTypes.OnboardingOpened,
  },
  ManualOnboardingTriggeredEvent: {
    displayedName: EventTypes.ManualOnboardingTriggered,
  },
  OnboardingStartedEvent: {
    displayedName: EventTypes.OnboardingStarted,
  },
  OnboardingRevertedEvent: {
    displayedName: EventTypes.OnboardingReverted,
  },
  OnboardingNavigateForwardEvent: {
    displayedName: EventTypes.OnboardingNavigateForward,
  },
  OnboardingNavigateBackwardEvent: {
    displayedName: EventTypes.OnboardingNavigateBackward,
  },
  OnboardingNavigateToSlideEvent: {
    displayedName: EventTypes.OnboardingNavigateToSlide,
    detail: eventDetail<number>(),
  },
  OnboardingClosedEvent: {
    displayedName: EventTypes.OnboardingClosed,
  },
  OnboardingFinalizedEvent: {
    displayedName: EventTypes.OnboardingFinalized,
  },

  OpenVoiceflowChatFromDashboardEvent: {
    displayedName: EventTypes.OpenVoiceflowChatFromDashboard,
  },

  OpenReportFromDentalRisk: {
    displayedName: EventTypes.OpenReportFromDentalRisk,
  },

  OpenDentalScanFromDentalRisk: {
    displayedName: EventTypes.OpenDentalScanFromDentalRisk,
  },

  PreviewProviderNetworkClick: {
    displayedName: EventTypes.PreviewProviderNetworkClick,
  },

  BackToPlansClick: {
    displayedName: EventTypes.BackToPlansClick,
  },

  ShowAddressSection: {
    displayedName: EventTypes.ShowAddressSection,
  },

  SelectPlanClick: {
    displayedName: EventTypes.SelectPlanClick,
    detail: eventDetail<{ plan: SubscriptionPlan }>(),
  },

  UpgradePlanClick: {
    displayedName: EventTypes.UpgradePlanClick,
    detail: eventDetail<{ plan: SubscriptionPlan }>(),
  },

  // Insurance
  SavingPlanReferralClick: {
    displayedName: EventTypes.SavingPlanReferralClick,
  },
  InsuranceInfoModalOpen: {
    displayedName: EventTypes.InsuranceInfoModalOpen,
  },
  InsuranceInfoModalClose: {
    displayedName: EventTypes.InsuranceInfoModalClose,
  },
  InsuranceServiceDetailsModalOpen: {
    displayedName: EventTypes.InsuranceServiceDetailsModalOpen,
    detail: eventDetail<InsuranceDetailsModalEventInterface>(),
  },
  InsuranceServiceDetailsModalClose: {
    displayedName: EventTypes.InsuranceServiceDetailsModalClose,
    detail: eventDetail<InsuranceDetailsModalEventInterface>(),
  },
  InsuranceFormModalOpen: {
    displayedName: EventTypes.InsuranceFormModalOpen,
  },
  InsuranceFormModalClose: {
    displayedName: EventTypes.InsuranceFormModalClose,
  },
  InsuranceSubmit: {
    displayedName: EventTypes.InsuranceSubmit,
  },
  InsuranceConnectSuccessModalOpen: {
    displayedName: EventTypes.InsuranceConnectSuccessModalOpen,
  },
  InsuranceConnectSuccessModalClose: {
    displayedName: EventTypes.InsuranceConnectSuccessModalClose,
  },
  InsuranceConnectFailModalOpen: {
    displayedName: EventTypes.InsuranceConnectFailModalOpen,
  },
  InsuranceConnectFailModalClose: {
    displayedName: EventTypes.InsuranceConnectFailModalClose,
  },
  InsuranceConnectPlanNotSupportedModalOpen: {
    displayedName: EventTypes.InsuranceConnectPlanNotSupportedModalOpen,
  },
  InsuranceConnectPlanNotSupportedModalClose: {
    displayedName: EventTypes.InsuranceConnectPlanNotSupportedModalClose,
  },
  DisconnectInsuranceClick: {
    displayedName: EventTypes.DisconnectInsuranceClick,
  },
  DisconnectInsuranceModalConfirm: {
    displayedName: EventTypes.DisconnectInsuranceModalConfirm,
  },
  DisconnectInsuranceModalCancel: {
    displayedName: EventTypes.DisconnectInsuranceModalCancel,
  },
  DisconnectInsuranceSuccessModalOpen: {
    displayedName: EventTypes.DisconnectInsuranceSuccessModalOpen,
  },
  DisconnectInsuranceSuccessModalClose: {
    displayedName: EventTypes.DisconnectInsuranceSuccessModalClose,
  },
  DisconnectInsuranceErrorModalOpen: {
    displayedName: EventTypes.DisconnectInsuranceErrorModalOpen,
  },
  DisconnectInsuranceErrorModalClose: {
    displayedName: EventTypes.DisconnectInsuranceErrorModalClose,
  },
  FindADentistSearchClick: {
    displayedName: EventTypes.FindADentistSearchClick,
  },
  OpenTermsModal: {
    displayedName: EventTypes.OpenTermsModal,
  },
  CloseTermsModal: {
    displayedName: EventTypes.CloseTermsModal,
  },

  // Support
  OpenSupportModal: {
    displayedName: EventTypes.OpenSupportModal,
  },
  CloseSupportModal: {
    displayedName: EventTypes.CloseSupportModal,
  },

  // Onboarding
  HideOnboardingPanelEvent: {
    displayedName: EventTypes.HideOnboardingPanelEvent,
  },
  ShowOnboardingPanelEvent: {
    displayedName: EventTypes.ShowOnboardingPanelEvent,
  },
  ToggleOnboardingPanelEvent: {
    displayedName: EventTypes.ToggleOnboardingPanelEvent,
  },
  OnboardingPanelCompletedEvent: {
    detail: eventDetail<{ id: string }>(),
    displayedName: EventTypes.OnboardingPanelCompletedEvent,
  },
});

export const {
  HomePageOpenedEvent,
  OpenSecondOpinionEvent,
  OpenAiPhotoScanEvent,
  OpenTalkToDentistEvent,
  ConfirmTalkToDentistEvent,
  CloseTalkToDentistEvent,
  OpenReportEvent,
  OpenProductRecommendationEvent,
  InfoCollectionOpenedEvent,
  InfoCollectionCompletedEvent,
  NavigateToPaymentLink,
  OpenUpgradePlanModal,
  CloseUpgradePlanModal,
  OpenDentalProviderUpgradePlanModal,
  CloseDentalProviderUpgradePlanModal,
  OpenConfirmUpgradePlanModal,
  CloseConfirmUpgradePlanModal,
  OnboardingOpenedEvent,
  ManualOnboardingTriggeredEvent,
  OnboardingStartedEvent,
  OnboardingRevertedEvent,
  OnboardingNavigateForwardEvent,
  OnboardingNavigateBackwardEvent,
  OpenPaymentSuccessModal,
  OnboardingNavigateToSlideEvent,
  OnboardingClosedEvent,
  OnboardingFinalizedEvent,
  OpenProductLinkEvent,
  OpenVoiceflowChatFromDashboardEvent,
  OpenReportFromDentalRisk,
  OpenDentalScanFromDentalRisk,
  PreviewProviderNetworkClick,
  BackToPlansClick,
  ShowAddressSection,
  SelectPlanClick,
  UpgradePlanClick,
  InsuranceInfoModalOpen,
  InsuranceInfoModalClose,
  InsuranceFormModalOpen,
  InsuranceFormModalClose,
  InsuranceSubmit,
  InsuranceConnectSuccessModalOpen,
  InsuranceConnectSuccessModalClose,
  InsuranceConnectFailModalOpen,
  InsuranceConnectFailModalClose,
  InsuranceConnectPlanNotSupportedModalOpen,
  InsuranceConnectPlanNotSupportedModalClose,
  InsuranceServiceDetailsModalOpen,
  InsuranceServiceDetailsModalClose,
  OpenProductDiscountModalEvent,
  CopyProductCouponEvent,
  SavingPlanReferralClick,
  DisconnectInsuranceClick,
  DisconnectInsuranceModalConfirm,
  DisconnectInsuranceModalCancel,
  DisconnectInsuranceSuccessModalOpen,
  DisconnectInsuranceSuccessModalClose,
  DisconnectInsuranceErrorModalOpen,
  DisconnectInsuranceErrorModalClose,
  FindADentistSearchClick,
  OpenTermsModal,
  CloseTermsModal,
  OpenSupportModal,
  CloseSupportModal,
  HideOnboardingPanelEvent,
  ShowOnboardingPanelEvent,
  ToggleOnboardingPanelEvent,
  OnboardingPanelCompletedEvent,
} = eventCreators;
